import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthHeader } from 'react-auth-kit';
import { Card, Form, Row, Col, Button, Alert, Spinner, Badge } from 'react-bootstrap';
import * as Icon from 'react-feather';

const REGIMEN_FISCAL_OPTIONS = [
    { value: '601', label: '601 - General de Ley Personas Morales' },
    { value: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
    { value: '606', label: '606 - Arrendamiento' },
    { value: '612', label: '612 - Personas Fisicas con Actividades Empresariales y Profesionales' },
    { value: '616', label: '616 - Sin obligaciones fiscales' },
    { value: '621', label: '621 - Incorporacion Fiscal' },
    { value: '625', label: '625 - Regimen de Plataformas Tecnologicas' },
    { value: '626', label: '626 - Regimen Simplificado de Confianza' },
];

function CartaPorteConfig() {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [config, setConfig] = useState({
        rfc_emisor: '',
        nombre_emisor: '',
        regimen_fiscal: '',
        codigo_postal_emisor: '',
        facturama_sandbox: true,
    });

    const [csdFiles, setCsdFiles] = useState({
        cer: null,
        key: null,
        password: '',
    });

    const [existingConfig, setExistingConfig] = useState(null);

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${apiUrl}/cartaporte/config`, {
                headers: { Authorization: authHeader() }
            });
            const data = res.data.msg;
            setExistingConfig(data);
            if (data.configured || data.rfc_emisor) {
                setConfig(prev => ({
                    ...prev,
                    rfc_emisor: data.rfc_emisor || '',
                    nombre_emisor: data.nombre_emisor || '',
                    regimen_fiscal: data.regimen_fiscal || '',
                    codigo_postal_emisor: data.codigo_postal_emisor || '',
                    facturama_sandbox: data.facturama_sandbox ?? true,
                }));
            }
        } catch (err) {
            setError('Error al cargar configuracion');
            console.error(err);
        }
        setLoading(false);
    };

    const handleChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    const handleFileChange = async (field, file) => {
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            const base64 = reader.result.split(',')[1];
            setCsdFiles(prev => ({ ...prev, [field]: base64 }));
        };
        reader.readAsDataURL(file);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        setSuccess(null);

        try {
            const payload = { ...config };

            // Include CSD files if provided
            if (csdFiles.cer) payload.csd_cer = csdFiles.cer;
            if (csdFiles.key) payload.csd_key = csdFiles.key;
            if (csdFiles.password) payload.csd_password = csdFiles.password;

            await axios.post(`${apiUrl}/cartaporte/config`, payload, {
                headers: { Authorization: authHeader() }
            });
            setSuccess('Configuracion guardada exitosamente');
            loadConfig();
        } catch (err) {
            setError(err.response?.data?.error || 'Error al guardar configuracion');
        }
        setSaving(false);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" />
                <p className="mt-2">Cargando configuracion...</p>
            </div>
        );
    }

    return (
        <div className="card mb-4">
            <div className="card-body">
                <div className="card-header d-flex justify-content-between align-items-center mb-3">
                    <h5 className="card-title mb-0">
                        <Icon.Settings size={20} className="me-2" />
                        Configuracion Carta Porte
                    </h5>
                    {existingConfig && (
                        <Badge bg={existingConfig.configured ? 'success' : 'warning'}>
                            {existingConfig.configured ? 'Configurado' : 'Incompleto'}
                        </Badge>
                    )}
                </div>

                {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

                {/* CSD Certificates */}
                <Card className="mb-3">
                    <Card.Header>
                        <h6 className="mb-0">Certificados CSD</h6>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Archivo .cer</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".cer"
                                        onChange={(e) => handleFileChange('cer', e.target.files[0])}
                                    />
                                    {existingConfig?.has_csd && <Form.Text className="text-success">Certificado cargado</Form.Text>}
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Archivo .key</Form.Label>
                                    <Form.Control
                                        type="file"
                                        accept=".key"
                                        onChange={(e) => handleFileChange('key', e.target.files[0])}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Password CSD</Form.Label>
                                    <Form.Control
                                        type="password"
                                        value={csdFiles.password}
                                        onChange={(e) => setCsdFiles(prev => ({ ...prev, password: e.target.value }))}
                                        placeholder="Contrasena del certificado"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Emisor Information */}
                <Card className="mb-3">
                    <Card.Header>
                        <h6 className="mb-0">Informacion del Emisor</h6>
                    </Card.Header>
                    <Card.Body>
                        <Row>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>RFC Emisor *</Form.Label>
                                    <Form.Control
                                        value={config.rfc_emisor}
                                        onChange={(e) => handleChange('rfc_emisor', e.target.value.toUpperCase())}
                                        placeholder="Ej: ABC123456AB1"
                                        maxLength={13}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nombre/Razon Social *</Form.Label>
                                    <Form.Control
                                        value={config.nombre_emisor}
                                        onChange={(e) => handleChange('nombre_emisor', e.target.value)}
                                        placeholder="Razon social"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Regimen Fiscal *</Form.Label>
                                    <Form.Select
                                        value={config.regimen_fiscal}
                                        onChange={(e) => handleChange('regimen_fiscal', e.target.value)}
                                    >
                                        <option value="">Seleccionar...</option>
                                        {REGIMEN_FISCAL_OPTIONS.map(opt => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.label}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={3}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Codigo Postal *</Form.Label>
                                    <Form.Control
                                        value={config.codigo_postal_emisor}
                                        onChange={(e) => handleChange('codigo_postal_emisor', e.target.value)}
                                        placeholder="Ej: 64000"
                                        maxLength={5}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>

                {/* Modo */}
                <Card className="mb-3">
                    <Card.Body>
                        <Form.Group>
                            <Form.Label>Modo de Facturacion</Form.Label>
                            <Form.Select
                                value={config.facturama_sandbox ? 'sandbox' : 'production'}
                                onChange={(e) => handleChange('facturama_sandbox', e.target.value === 'sandbox')}
                            >
                                <option value="sandbox">Sandbox (Pruebas)</option>
                                <option value="production">Produccion</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Sandbox genera facturas de prueba. Cambia a Produccion cuando estes listo para emitir facturas reales.
                            </Form.Text>
                        </Form.Group>
                    </Card.Body>
                </Card>

                {/* Save Button */}
                <div className="text-end">
                    <Button variant="primary" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <>
                                <Spinner as="span" animation="border" size="sm" className="me-2" />
                                Guardando...
                            </>
                        ) : (
                            <>
                                <Icon.Save size={16} className="me-1" />
                                Guardar Configuracion
                            </>
                        )}
                    </Button>
                </div>
            </div>
        </div>
    );
}

export default CartaPorteConfig;
